import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faHandsHelping, faIndustry, faShip } from '@fortawesome/free-solid-svg-icons';
import { OrganizationType } from 'modules/Common/utils/Utility';
import { useOrganization, useOrganizationList, useUser, useClerk } from '@clerk/clerk-react';
import { PriceSlider, PricingTable } from './PricingTable';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { stripePromise } from '../_app';
import http from 'helpers/requests';
import { Redirect, useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import CgButton from 'modules/Common/ui/CgButton';
import { cgToast } from 'modules/Common/ui/toast';
import { useFreemium } from 'modules/Common/freemium/FreemiumContext';
import { useBranding } from '../hooks/useBranding';

const FreePlanForm = ({ onClose, orgType }) => {
  const history = useHistory();
  const brand = useBranding();
  const { t } = useTranslation('app');
  let { organization } = useOrganization();
  const { userMemberships, setActive } = useOrganizationList({
    userMemberships: { pageSize: 10 },
  });
  const { user: clerkUser } = useClerk();
  const [orgName, setOrgName] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  return (
    <div className="flex justify-center">
      <div className="mt-3 shadow-medium text-center w-[1000px] bg-white">
        <div className="items-center px-4 py-2">
          <div className="space-y-4 p-4">
            {/* Organization Name Field */}
            <div className="flex flex-col">
              <label
                htmlFor="orgName"
                className="mb-3 text-xl font-medium text-gray-700"
              >
                {t('app:payment.createOrganization', 'Create Organization')}
              </label>
              <input
                type="text"
                id="orgName"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primaryColor focus:border-primaryColor focus:ring-opacity-25 focus:*:border-opacity-25"
                placeholder={t('app:payment.orgNamePlaceholder', 'Enter organization name')}
              />
            </div>

            {/* Terms Acceptance Checkbox */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="h-4 w-4 accent-primaryColor focus:ring-primaryColor border-gray-300 rounded"
              />
              <label
                htmlFor="acceptTerms"
                className="text-sm text-gray-700"
              >
                {t('app:payment.agreePrefix', 'I agree to the')}{' '}
                <a
                  href={brand.tosUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primaryColor hover:text-primaryColor underline"
                >
                  {t('app:payment.termsOfService', 'Terms of Service')}
                </a>{' '}
                {t('app:payment.agreeConnector', 'and')}{' '}
                <a
                  href="https://app.termly.io/policy-viewer/policy.html?policyUUID=711ef90c-30fe-451b-86d7-c48ebfb3e29c"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primaryColor hover:text-primaryColor underline"
                >
                  {t('app:payment.privacyPolicy', 'Privacy Policy')}
                </a>
              </label>
            </div>
          </div>
        </div>
        <div className="flex pb-4 justify-between px-11">
          <CgButton
            size="md"
            variant="secondary"
            label={t('app:payment.backToSelection', 'Back to Selection')}
            icon="faArrowLeft"
            onClick={onClose}
            id="close-modal"
          ></CgButton>
          <CgButton
            size="md"
            variant="primary"
            label={t('app:payment.createOrganization', 'Create Organization')}
            icon="faCirclePlus"
            disabled={!acceptTerms}
            onClick={async () => {
              try {
                let res = await http.post('/payments/free-plan', {
                  orgName,
                  orgType,
                });
                const check = async () => {
                  if (!organization) {
                    let orgMemberships = await clerkUser.getOrganizationMemberships({
                      pageSize: 10,
                    });
                    let userOrgs = userMemberships?.data;
                    if (userOrgs.length == 0) {
                      userOrgs = orgMemberships.data;
                    }
                    if (userOrgs?.length > 0) {
                      organization = userOrgs[0].organization;
                      await setActive({
                        organization: organization.id,
                      });
                    }
                  }
                  return Boolean(organization);
                };
                while (!(await check())) {
                  await new Promise((resolve) => setTimeout(resolve, 1000));
                }
                if (res.status == 200 && organization) {
                  window.location.replace('/');
                }
              } catch (err) {
                cgToast.apiError(err);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};
export const CheckoutForm = ({ priceId, onClose, quantity }) => {
  const { t } = useTranslation('app');
  const fetchClientSecret = useCallback(async () => {
    // Create a Checkout Session
    let res = await http.post('/payments/create-checkout-session', {
      priceId,
      quantity,
    });
    return res.data.clientSecret;
  }, [priceId]);

  const options = { fetchClientSecret };

  return (
    <div className="mt-6 w-full flex justify-center px-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-3xl p-6 sm:p-8 md:p-10">
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={options}
        >
          <div className="w-full min-h-[640px] pt-2">
            <EmbeddedCheckout className="Custom w-full" />
          </div>
        </EmbeddedCheckoutProvider>
        <div className="mt-6">
          <button
            id="close-modal"
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            {t('app:payment.backToSelection', 'Back to Selection')}
          </button>
        </div>
      </div>
    </div>
  );
};
const UserTypeButton = ({ title, icon, description, onClick }) => (
  <button
    onClick={onClick}
    className="w-64 h-80 p-6 bg-white rounded-lg shadow-medium flex flex-col items-center justify-center space-y-4 transition-transform hover:scale-105"
  >
    <FontAwesomeIcon
      icon={icon}
      size="6x"
      className="text-primaryColor"
    />

    <span className="text-2xl font-semibold text-primaryColor">{title}</span>
    <p className="text-secondaryColor">{description}</p>
  </button>
);

const PaymentPage = () => {
  const brand = useBranding();
  const { t } = useTranslation('app');
  const [selectedUserType, setSelectedUserType] = useState(null);
  const { user } = useUser();
  const [units, setUnits] = useState(5);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showFreePlan, setShowFreePlan] = useState(false);
  const [selectedPriceId, setSelectedPriceId] = useState(null);
  const history = useHistory();
  if (brand.key !== 'carbonglance') return <Redirect to="/" />;

  const handleUnitChange = (e) => {
    setUnits(parseInt(e.target.value));
  };

  const handleSubscribe = (priceId) => {
    setSelectedPriceId(priceId);
    setShowCheckout(true);
  };

  const declarantPlans = [
    {
      title: t('app:payment.plans.essentials.title', 'CBAM Essentials'),
      description: t(
        'app:payment.plans.essentials.description',
        'Automate your CBAM compliance & reporting in minutes',
      ),
      // basePrice: 150,
      // included: 5,
      // perUnitPrice: 30,
      features: [
        t(
          'app:payment.plans.essentials.feature1',
          'Upload your data all at once without manual data inputs',
        ),
        t('app:payment.plans.essentials.feature2', 'Request and collect data from suppliers'),
        t(
          'app:payment.plans.essentials.feature3',
          'Generate your quarterly CBAM Reports with a few clicks',
        ),
      ],
      // unitName: "shipment",
      included: 0,
      time: 'year',
      action: 'demo',
      buttonText: t('app:payment.plans.essentials.button', 'Book a Demo'),
      isPopular: false,
      isEnterprise: true,
    },
    // {
    //   title: "CBAM Enterprise",
    //   description:
    //     "Importing more than 50 shipments per quarter or handling multiple subsidiaries? Contact us for a custom quote",
    //   features: [
    //     "Everything in the previous tier",
    //     "Tier 1 suppliers access to operator tool",
    //     "Tailored support",
    //   ],
    //   buttonText: "Contact us",
    //   time: "quarter",
    //   isPopular: false,
    //   isEnterprise: true,
    // },
  ];

  const operatorPlans = [
    {
      title: t('app:payment.plans.free.title', 'Free Plan'),
      description: t(
        'app:payment.plans.free.description',
        'Calculate your CBAM emissions data in minutes',
      ),
      features: [
        t(
          'app:payment.plans.free.feature1',
          "Map your factory's CBAM emission sources with a digital schematic diagram",
        ),
        t('app:payment.plans.free.feature2', 'Automate CBAM emission calculations'),
        t('app:payment.plans.free.feature3', 'Send and track supplier emissions data requests'),
        t(
          'app:payment.plans.free.feature4',
          'Compare your product emissions against country-specific default values',
        ),
        t('app:payment.plans.free.feature5', 'Access step-by-step tutorials & Help Centre'),
        t('app:payment.plans.free.feature6', 'Limited to 1 draft diagram'),
        t('app:payment.plans.free.feature7', 'Limited to 1 installation'),
      ],
      included: 1,
      unitName: 'installation',
      time: 'year',
      action: 'free',
      buttonText: t('app:payment.plans.free.button', 'Try it out'),
      basePrice: 0,
      perUnitPrice: 0,
      isFree: true,
    },
    {
      title: t('app:payment.plans.monitoring.title', 'Monitoring & Reporting Tool'),
      description: t(
        'app:payment.plans.monitoring.description',
        'Calculate your CBAM emissions data in minutes',
      ),
      features: [
        t(
          'app:payment.plans.monitoring.feature1',
          "Map your factory's CBAM emission sources with a digital schematic diagram",
        ),
        t('app:payment.plans.monitoring.feature2', 'Automate CBAM emission calculations'),
        t(
          'app:payment.plans.monitoring.feature3',
          'Send and track supplier emissions data requests',
        ),
        t(
          'app:payment.plans.monitoring.feature4',
          'Compare your product emissions against country-specific default values',
        ),
        t('app:payment.plans.monitoring.feature5', 'Access step-by-step tutorials & Help Centre'),
        t(
          'app:payment.plans.monitoring.feature6',
          'Generate the CBAM-required data for your clients',
        ),
      ],
      included: 1,
      unitName: 'installation',
      time: 'year',
      action: 'subscribe',
      buttonText: t('app:payment.plans.monitoring.button', 'Subscribe'),
      basePrice: 500,
      perUnitPrice: 500,
    },
  ];
  const consultantPlans = [
    {
      title: t('app:payment.plans.consultant.title', 'Consultant Plan'),
      description: t(
        'app:payment.plans.consultant.description',
        'Tailored packages to help scale your consulting business.',
      ),
      features: [
        t('app:payment.plans.consultant.feature1', 'Automate CBAM reporting for your EU clients'),
        t(
          'app:payment.plans.consultant.feature2',
          'Calculate emissions data for your non-EU clients in minutes',
        ),
      ],
      included: 0,
      time: 'year',
      unitName: 'client',
      action: 'demo',
      buttonText: t('app:payment.plans.consultant.button', 'Book a Demo'),
      isEnterprise: true,
    },
  ];
  const possiblePlans = {
    [OrganizationType.Declarant]: declarantPlans,
    [OrganizationType.Operator]: operatorPlans,
    [OrganizationType.Consultant]: consultantPlans,
  };
  const possibleUnits = {
    [OrganizationType.Declarant]: t('app:payment.unitShipments', 'shipments'),
    [OrganizationType.Operator]: t('app:payment.unitInstallations', 'installations'),
    [OrganizationType.Consultant]: t('app:payment.unitClients', 'clients'),
  };
  const unitName = possibleUnits[selectedUserType];
  const plans = possiblePlans[selectedUserType];
  const getStep = () => {
    if (showFreePlan) {
      return (
        <FreePlanForm
          orgType={selectedUserType}
          onClose={() => setShowFreePlan(false)}
        />
      );
    }
    if (showCheckout) {
      return (
        <CheckoutForm
          priceId={selectedPriceId}
          quantity={units}
          onClose={() => setShowCheckout(false)}
        />
      );
    }
    if (selectedUserType) {
      return (
        <div className="space-y-8">
          <div className="p-8 justify-center items-center">
            {selectedUserType == OrganizationType.Operator && (
              <PriceSlider
                value={units}
                onChange={handleUnitChange}
                max={25}
                unitName={unitName}
              />
            )}
            <div className="flex justify-center gap-8">
              {plans.map((plan, index) => (
                <PricingTable
                  key={index}
                  {...plan}
                  units={units}
                  onSubscribe={() => {
                    // Stable action key drives routing; buttonText is display-only
                    // and localized, so it must never be compared here.
                    if (plan.action === 'demo') {
                      window.open('https://calendly.com/victor-e-0/product-demo', '_blank');
                      return;
                    }
                    if (plan.action !== 'subscribe') {
                      // free plan
                      // navigate to /create-organization using useHistory
                      setShowFreePlan(true);
                    } else {
                      let priceId;
                      if (process.env.REACT_APP_ENV_MODE == 'PROD') {
                        priceId = 'price_1TByCyRwsLdKL2q4MmdH9mNa';
                      } else {
                        priceId = 'price_1TByCYRwsLdKL2q4vwz68Zen';
                      }
                      handleSubscribe(priceId);
                    }
                  }}
                />
              ))}
            </div>
          </div>
          <button
            onClick={() => setSelectedUserType(null)}
            className="block mx-auto bg-[#00b894] text-white py-2 px-4 rounded hover:bg-[#00a583] transition-colors"
          >
            {t('app:payment.backToSelection', 'Back to Selection')}
          </button>
        </div>
      );
    } else {
      return (
        <div className="space-y-8">
          <h2 className="text-3xl font-bold text-secondaryColor text-center">
            {t('app:payment.chooseCompanyType', 'Choose Your Company Type')}
          </h2>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-8">
            <UserTypeButton
              title={t('app:orgType.declarant', 'Declarant')}
              icon={faFile}
              description={t(
                'app:payment.declarantQuestion',
                'Do you import CBAM goods into the EU?',
              )}
              onClick={() => {
                setSelectedUserType(OrganizationType.Declarant);
              }}
            />
            <UserTypeButton
              title={t('app:orgType.operator', 'Operator')}
              icon={faIndustry}
              description={t(
                'app:payment.operatorQuestion',
                'Do you produce and/or sell CBAM goods?',
              )}
              onClick={() => {
                setSelectedUserType(OrganizationType.Operator);
                setUnits(1);
              }}
            />
            <UserTypeButton
              title={t('app:orgType.consultant', 'Consultant')}
              icon={faHandsHelping}
              description={t(
                'app:payment.consultantQuestion',
                'Do you assist CBAM-affected businesses?',
              )}
              onClick={() => {
                setSelectedUserType(OrganizationType.Consultant);
              }}
            />
          </div>
        </div>
      );
    }
  };
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-6 px-4 md:px-12 lg:px-24 xl:px-48">
      <div className="w-full mt-4 space-y-8">{getStep()}</div>
    </div>
  );
};

export default PaymentPage;
